import type {
  ExperimentArm,
  ExperimentIndex,
  ExperimentLayout,
  ExperimentManifest,
  ExperimentMediaType,
  ExperimentPlayground,
  ExperimentSummary,
  MediaRef,
  VideoRef,
} from "./types";

export const EXPERIMENTS_INDEX_KEY = "experiments/index.json";

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const str = (value: unknown): string | undefined =>
  typeof value === "string" && value.trim() ? value : undefined;

const parseKind = (value: unknown): ExperimentArm["kind"] =>
  value === "source" || value === "control" || value === "candidate"
    ? value
    : undefined;

const parseMediaType = (value: unknown): ExperimentMediaType =>
  value === "video" ? "video" : "image";

export function parseMediaRef(value: unknown): MediaRef | null {
  if (!isObject(value)) return null;
  const bucket = str(value.bucket);
  const key = str(value.key);
  if (!bucket || !key) return null;
  const ref: MediaRef = { bucket, key };
  if (typeof value.width === "number") ref.width = value.width;
  if (typeof value.height === "number") ref.height = value.height;
  return ref;
}

export function parseVideoRef(value: unknown): VideoRef | null {
  const ref = parseMediaRef(value);
  if (!ref || !isObject(value)) return null;
  const video: VideoRef = { ...ref };
  if (typeof value.duration === "number" && value.duration > 0)
    video.duration = value.duration;
  const contentType = str(value.content_type);
  if (contentType) video.content_type = contentType;
  if (value.audio_only === true) video.audio_only = true;
  return video;
}

const parseLayout = (value: unknown): ExperimentLayout =>
  value === "gallery" ? "gallery" : "compare";

const parseRefMap = <T>(
  value: unknown,
  parse: (raw: unknown) => T | null,
): Record<string, T> => {
  const out: Record<string, T> = {};
  if (!isObject(value)) return out;
  for (const [key, raw] of Object.entries(value)) {
    const ref = parse(raw);
    if (ref) out[key] = ref;
  }
  return out;
};

/** A video experiment plays films; everything else is the photo gallery. */
export const isVideoExperiment = (manifest: ExperimentManifest) =>
  manifest.media_type === "video" && Object.keys(manifest.videos).length > 0;

/** Every item on one page with its own players; only film entries can ask for it. */
export const isGalleryExperiment = (manifest: ExperimentManifest) =>
  manifest.layout === "gallery" && isVideoExperiment(manifest);

export function parseIndex(value: unknown): ExperimentIndex {
  if (!isObject(value) || !Array.isArray(value.experiments)) {
    throw new Error(
      "Experiment index is malformed (expected { experiments: [...] })",
    );
  }
  const experiments: ExperimentSummary[] = [];
  for (const raw of value.experiments) {
    if (!isObject(raw)) continue;
    const slug = str(raw.slug);
    const title = str(raw.title);
    const manifest = parseMediaRef(raw.manifest);
    if (!slug || !title || !manifest) continue;
    experiments.push({
      slug,
      title,
      manifest,
      date: str(raw.date) ?? "",
      author: str(raw.author),
      summary: str(raw.summary),
      tags: Array.isArray(raw.tags)
        ? raw.tags.filter((t): t is string => typeof t === "string")
        : undefined,
      cover: parseMediaRef(raw.cover) ?? undefined,
      media_type: parseMediaType(raw.media_type),
      photo_count:
        typeof raw.photo_count === "number" ? raw.photo_count : undefined,
      arm_count: typeof raw.arm_count === "number" ? raw.arm_count : undefined,
    });
  }
  experiments.sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0));
  return { version: 1, experiments };
}

export function parseManifest(value: unknown): ExperimentManifest {
  if (!isObject(value)) throw new Error("Experiment manifest is malformed");
  const slug = str(value.slug);
  const title = str(value.title);
  if (!slug || !title)
    throw new Error("Experiment manifest needs a slug and a title");
  if (
    !Array.isArray(value.arms) ||
    !Array.isArray(value.photos) ||
    !(isObject(value.images) || isObject(value.videos))
  ) {
    throw new Error("Experiment manifest needs arms, photos and images");
  }
  const arms = value.arms
    .filter(isObject)
    .map((arm) => ({
      id: str(arm.id) ?? "",
      label: str(arm.label) ?? str(arm.id) ?? "",
      description: str(arm.description),
      kind: parseKind(arm.kind),
    }))
    .filter((arm) => arm.id);
  const photos = value.photos
    .filter(isObject)
    .map((photo) => ({
      id: str(photo.id) ?? "",
      label: str(photo.label),
      caption: str(photo.caption),
    }))
    .filter((photo) => photo.id);
  const images = parseRefMap(value.images, parseMediaRef);
  const videos = parseRefMap(value.videos, parseVideoRef);
  if (!arms.length || !photos.length)
    throw new Error("Experiment manifest has no arms or no photos");
  let metrics: ExperimentManifest["metrics"];
  if (
    isObject(value.metrics) &&
    Array.isArray(value.metrics.columns) &&
    Array.isArray(value.metrics.rows)
  ) {
    metrics = {
      columns: value.metrics.columns
        .filter(isObject)
        .map((c) => ({
          key: str(c.key) ?? "",
          label: str(c.label) ?? str(c.key) ?? "",
          help: str(c.help),
        }))
        .filter((c) => c.key),
      rows: value.metrics.rows
        .filter(isObject)
        .map((r) => ({
          arm: str(r.arm) ?? "",
          values: isObject(r.values)
            ? (r.values as Record<string, string | number | null>)
            : {},
        }))
        .filter((r) => r.arm),
    };
  }
  return {
    version: 1,
    slug,
    title,
    date: str(value.date) ?? "",
    author: str(value.author),
    summary: str(value.summary),
    description_md: str(value.description_md),
    tags: Array.isArray(value.tags)
      ? value.tags.filter((t): t is string => typeof t === "string")
      : undefined,
    media_type: parseMediaType(value.media_type),
    layout: parseLayout(value.layout),
    arms,
    photos,
    images,
    videos,
    metrics,
    links: Array.isArray(value.links)
      ? value.links
          .filter(isObject)
          .map((l) => ({ label: str(l.label) ?? "", url: str(l.url) ?? "" }))
          .filter((l) => l.label && l.url)
      : undefined,
    playground: parsePlayground(value.playground),
  };
}

const finite = (value: unknown): number | undefined =>
  typeof value === "number" && Number.isFinite(value) ? value : undefined;

/**
 * The manifest's optional `playground` block. Anything malformed drops the block (no playground)
 * rather than half-rendering it; the dashboard API validates it again on every request.
 */
export function parsePlayground(
  value: unknown,
): ExperimentPlayground | undefined {
  if (!isObject(value)) return undefined;
  const loras = (Array.isArray(value.loras) ? value.loras : [])
    .filter(isObject)
    .map((raw) => ({
      id: str(raw.id) ?? "",
      label: str(raw.label) ?? str(raw.id) ?? "",
      file: typeof raw.file === "string" && raw.file ? raw.file : null,
      description: str(raw.description),
    }))
    .filter((lora) => lora.id);
  const prompts = (Array.isArray(value.prompts) ? value.prompts : [])
    .filter(isObject)
    .map((raw) => ({
      id: str(raw.id) ?? "",
      label: str(raw.label) ?? str(raw.id) ?? "",
      template: str(raw.template) ?? "",
      description: str(raw.description),
    }))
    .filter((prompt) => prompt.id && prompt.template);
  if (!loras.length || !prompts.length) return undefined;
  const range = isObject(value.strength) ? value.strength : {};
  const min = finite(range.min) ?? 0;
  const max = finite(range.max) ?? 1.5;
  if (!(min >= 0 && max > min)) return undefined;
  const step = finite(range.step) ?? 0.05;
  const clamp = (n: number) => Math.min(max, Math.max(min, n));
  const fallback = finite(range.default) ?? Math.min(max, 0.8);
  const sweep = (
    Array.isArray(value.sweep_strengths) ? value.sweep_strengths : []
  )
    .map(finite)
    .filter((n): n is number => n !== undefined && n >= min && n <= max)
    .slice(0, 8);
  const seeds: Record<string, number> = {};
  if (isObject(value.seeds)) {
    for (const [photo, seed] of Object.entries(value.seeds)) {
      if (Number.isSafeInteger(seed) && (seed as number) >= 0)
        seeds[photo] = seed as number;
    }
  }
  const defaults = isObject(value.defaults) ? value.defaults : {};
  return {
    title: str(value.title),
    description: str(value.description),
    loras,
    prompts,
    allowCustomPrompt: value.allow_custom_prompt === true,
    strength: {
      min,
      max,
      step: step > 0 ? step : 0.05,
      default: clamp(fallback),
    },
    sweepStrengths: sweep,
    defaults: { lora: str(defaults.lora), prompt: str(defaults.prompt) },
    seeds,
  };
}

/** Arms in display order: sources first, then controls, then candidates, keeping the manifest order inside each group. */
export function orderedArms(manifest: ExperimentManifest) {
  const rank = (kind?: string) =>
    kind === "source" ? 0 : kind === "control" ? 1 : 2;
  return [...manifest.arms].sort((a, b) => rank(a.kind) - rank(b.kind));
}

export function formatMetric(value: string | number | null | undefined) {
  if (value === null || value === undefined) return "–";
  if (typeof value === "number")
    return Number.isInteger(value) ? String(value) : value.toFixed(2);
  return value;
}
